import { jsx as _jsx } from "react/jsx-runtime";
import Loader from '@ui/components/Loader';
import Layout from '@ui/layout';
import { EventRegistrationLayout } from '@ui/layout/EventRegistration';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import EventRegistrationProtectedRoute from './EventRegistrationProtectedRoute';
import ProtectedRoute from './ProtectedRoute';
import Users from '@ui/pages/Users';
import Collaborators from '@ui/pages/Collaborators';
import Roles from '@ui/pages/Roles';
import CreateEvent from '@ui/pages/Events/CreateEvent';
import EventsHomePage from '@ui/pages/Events/EventsHomePage';
import EventDetails from '@ui/pages/Events/EventsHomePage/EventDetails';
import Task from '@ui/pages/Events/CreateEvent/Task';
import Tasks from '@ui/pages/Tasks';
import HomeExample from '@ui/pages/HomeExample';
import People from '@ui/pages/People';
import PersonDetail from '@ui/pages/People/PersonDetail';
import RelationsManagement from '@ui/pages/Relations';
import RelationDetail from '@ui/pages/Relations/RelationDetail';
import RolePermissionsDetail from '@ui/pages/Roles/Permissions';
import GlobalSearchPage from '@ui/pages/GlobalSearch';
import Settings from '@ui/pages/Settings';
import UsageManualPage from '@ui/pages/UsageManual';
import Sponsorships from '@ui/pages/Sponsorships';
import NewSponsorship from '@ui/pages/Sponsorships/NewSponsorship';
import SponsorshipDetails from '@ui/pages/Sponsorships/Sponsorship-Details';
import TicketDetail from '@ui/pages/Sponsorships/Sponsorship-Details/EventsTab/TicketDetail';
import PreviewEventForm from '@ui/pages/Events/PreviewEventForm';
const loadComponent = (factory) => {
    return async () => {
        const module = await factory();
        return { Component: module.default };
    };
};
export const router = createBrowserRouter([
    // Public routes (outside ProtectedRoute)
    {
        path: '/login',
        lazy: loadComponent(() => import('@ui/pages/Login'))
    },
    {
        path: '/unauthorized',
        lazy: async () => {
            const module = await import('@ui/pages/Unauthorized');
            const WrappedUnauthorized = () => {
                const { useLocation } = require('react-router-dom');
                const location = useLocation();
                const isPermissionDenial = location.state?.reason === 'unauthorized_access';
                if (isPermissionDenial) {
                    return _jsx(module.default, { message: 'You do not have permission to access this page.', sessionTimeout: false });
                }
                return _jsx(module.default, { message: 'Session has timed out. Redirecting in', sessionTimeout: true });
            };
            return {
                Component: WrappedUnauthorized
            };
        }
    },
    // Protected routes
    {
        element: _jsx(ProtectedRoute, {}),
        children: [
            {
                element: _jsx(Layout, {}),
                children: [
                    { path: '/', lazy: loadComponent(() => import('@ui/pages/Home')) },
                    { path: '/home-example', element: _jsx(HomeExample, {}) },
                    { path: '/tasks', element: _jsx(Tasks, {}) },
                    {
                        path: '/event-tasks',
                        element: _jsx(Task, {})
                    },
                    {
                        path: '/event-tasks/:eventId',
                        element: _jsx(Task, {})
                    },
                    { path: '/people', element: _jsx(People, {}) },
                    {
                        path: '/people/:id',
                        element: _jsx(PersonDetail, {})
                    },
                    { path: '/relations', element: _jsx(RelationsManagement, {}) },
                    {
                        path: '/relations/:id',
                        element: _jsx(RelationDetail, {})
                    },
                    { path: '/users', element: _jsx(Users, {}) },
                    { path: '/collaborators', element: _jsx(Collaborators, {}) },
                    { path: '/roles-permissions', element: _jsx(Roles, {}) },
                    {
                        path: '/roles-permissions/new',
                        element: _jsx(RolePermissionsDetail, {})
                    },
                    {
                        path: '/roles-permissions/:roleId/permissions',
                        element: _jsx(RolePermissionsDetail, {})
                    },
                    {
                        path: '/create-event',
                        element: _jsx(CreateEvent, {})
                    },
                    { path: '/events', element: _jsx(EventsHomePage, {}) },
                    {
                        path: '/event-details/:eventId/:type?',
                        element: _jsx(EventDetails, {})
                    },
                    {
                        path: '/sponsorships',
                        element: _jsx(Sponsorships, {})
                    },
                    {
                        path: '/sponsorships/new-sponsorship',
                        element: _jsx(NewSponsorship, {})
                    },
                    {
                        path: '/sponsorship-details/:id',
                        element: _jsx(SponsorshipDetails, {})
                    },
                    {
                        path: '/sponsorship-details/:id/ticket-detail/:eventId',
                        element: _jsx(TicketDetail, {})
                    },
                    {
                        path: '/global-search',
                        element: _jsx(GlobalSearchPage, {})
                    },
                    {
                        path: '/settings',
                        element: _jsx(Settings, {})
                    },
                    {
                        path: '/usage-manual',
                        element: _jsx(UsageManualPage, {})
                    },
                    {
                        path: '/notifications',
                        lazy: loadComponent(() => import('@ui/pages/Notifications'))
                    }
                ]
            }
        ]
    },
    {
        element: _jsx(ProtectedRoute, {}),
        children: [
            {
                path: 'event/preview/:e',
                element: _jsx(PreviewEventForm, {})
            }
        ]
    },
    {
        element: _jsx(EventRegistrationLayout, {}),
        children: [
            {
                element: _jsx(EventRegistrationProtectedRoute, {}),
                children: [
                    {
                        path: '/event-registration/:e',
                        lazy: loadComponent(() => import('@ui/pages/EventRegistration/Registration'))
                    },
                    {
                        path: '/event-registration/:e/faqs',
                        lazy: loadComponent(() => import('@ui/pages/EventRegistration/Faqs/redirect'))
                    },
                    {
                        path: '/event-registration/:e/form',
                        lazy: loadComponent(() => import('@ui/pages/EventRegistration/RegistrationForm'))
                    },
                    {
                        path: '/event-registration/:e/success',
                        lazy: loadComponent(() => import('@ui/pages/EventRegistration/Success'))
                    },
                    {
                        path: '/event-registration/:e/expiredLink',
                        lazy: loadComponent(() => import('@ui/pages/EventRegistration/ExpiredLink'))
                    }
                ]
            }
        ]
    },
    {
        path: '*',
        element: _jsx(Navigate, { to: '/', replace: true })
    }
]);
const AppRoutes = () => {
    return _jsx(RouterProvider, { router: router, fallbackElement: _jsx(Loader, {}) });
};
export default AppRoutes;
